import { handleActions } from 'redux-actions';
import immutable from 'immutability-helper';
import { ActionTypes } from '../constants';

// State awal untuk tagihan dengan format object array
export const billState = {
    status: 'idle',
    selectedBills: {
        items: [],           // Array of bill objects
        total: 0,            // Total jumlah tagihan terpilih
        totalAmount: 0,      // Total nominal tagihan terpilih
        selectedIds: [],     // Array of ID untuk memudahkan pengecekan
        summary: {
            count: 0,
            amount: 0,
            categories: {},  // Grup berdasarkan kategori jika ada
        }
    },
    lastUpdated: null,
    error: null,
};

export default handleActions(
    {
        // Reset semua state tagihan
        [ActionTypes.RESET_BILL_STATE]: (state) => immutable(state, {
            status: { $set: 'idle' },
            selectedBills: {
                $set: {
                    items: [],
                    total: 0,
                    totalAmount: 0,
                    selectedIds: [],
                    summary: {
                        count: 0,
                        amount: 0,
                        categories: {},
                    }
                }
            },
            lastUpdated: { $set: null },
            error: { $set: null },
        }),

        // Memilih satu tagihan
        [ActionTypes.SELECT_BILL]: (state, { payload }) => {
            // payload adalah object bill
            const newItems = [payload];
            const totalAmount = payload.amount || 0;

            return immutable(state, {
                status: { $set: 'bill_selected' },
                selectedBills: {
                    $set: {
                        items: newItems,
                        total: 1,
                        totalAmount: totalAmount,
                        selectedIds: [payload.id],
                        summary: {
                            count: 1,
                            amount: totalAmount,
                            categories: {
                                [payload.category || 'uncategorized']: {
                                    count: 1,
                                    amount: totalAmount,
                                    items: [payload]
                                }
                            }
                        }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
                error: { $set: null },
            });
        },

        // Memilih multiple tagihan
        [ActionTypes.SELECT_MULTIPLE_BILLS]: (state, { payload }) => {
            // payload adalah array of bill objects
            const items = Array.isArray(payload) ? payload : [payload];
            const total = items.length;
            const totalAmount = items.reduce((sum, bill) => sum + (bill.amount || 0), 0);
            const selectedIds = items.map(bill => bill.id);

            // Buat summary berdasarkan kategori
            const categories = {};
            items.forEach(bill => {
                const category = bill.category || 'uncategorized';
                if (!categories[category]) {
                    categories[category] = {
                        count: 0,
                        amount: 0,
                        items: []
                    };
                }
                categories[category].count += 1;
                categories[category].amount += (bill.amount || 0);
                categories[category].items.push(bill);
            });

            return immutable(state, {
                status: { $set: 'bills_selected' },
                selectedBills: {
                    $set: {
                        items: items,
                        total: total,
                        totalAmount: totalAmount,
                        selectedIds: selectedIds,
                        summary: {
                            count: total,
                            amount: totalAmount,
                            categories: categories,
                        }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
                error: { $set: null },
            });
        },

        // Toggle selection (tambah/hapus dari pilihan)
        [ActionTypes.TOGGLE_BILL_SELECTION]: (state, { payload }) => {
            const currentItems = state.selectedBills.items || [];
            const existingIndex = currentItems.findIndex(bill => bill.id === payload.id);

            let newItems;
            if (existingIndex >= 0) {
                // Hapus dari pilihan
                newItems = currentItems.filter(bill => bill.id !== payload.id);
            } else {
                // Tambahkan ke pilihan
                newItems = [...currentItems, payload];
            }

            const total = newItems.length;
            const totalAmount = newItems.reduce((sum, bill) => sum + (bill.amount || 0), 0);
            const selectedIds = newItems.map(bill => bill.id);

            // Update summary berdasarkan kategori
            const categories = {};
            newItems.forEach(bill => {
                const category = bill.category || 'uncategorized';
                if (!categories[category]) {
                    categories[category] = {
                        count: 0,
                        amount: 0,
                        items: []
                    };
                }
                categories[category].count += 1;
                categories[category].amount += (bill.amount || 0);
                categories[category].items.push(bill);
            });

            return immutable(state, {
                status: { $set: total > 0 ? 'bills_selected' : 'idle' },
                selectedBills: {
                    $set: {
                        items: newItems,
                        total: total,
                        totalAmount: totalAmount,
                        selectedIds: selectedIds,
                        summary: {
                            count: total,
                            amount: totalAmount,
                            categories: categories,
                        }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
                error: { $set: null },
            });
        },

        // Menghapus tagihan dari daftar pilihan (spesifik)
        [ActionTypes.REMOVE_BILL_FROM_SELECTION]: (state, { payload }) => {
            const currentItems = state.selectedBills.items || [];
            const newItems = currentItems.filter(bill => bill.id !== payload.id);

            const total = newItems.length;
            const totalAmount = newItems.reduce((sum, bill) => sum + (bill.amount || 0), 0);
            const selectedIds = newItems.map(bill => bill.id);

            // Update summary
            const categories = {};
            newItems.forEach(bill => {
                const category = bill.category || 'uncategorized';
                if (!categories[category]) {
                    categories[category] = {
                        count: 0,
                        amount: 0,
                        items: []
                    };
                }
                categories[category].count += 1;
                categories[category].amount += (bill.amount || 0);
                categories[category].items.push(bill);
            });

            return immutable(state, {
                status: { $set: total > 0 ? 'bills_selected' : 'idle' },
                selectedBills: {
                    $set: {
                        items: newItems,
                        total: total,
                        totalAmount: totalAmount,
                        selectedIds: selectedIds,
                        summary: {
                            count: total,
                            amount: totalAmount,
                            categories: categories,
                        }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
                error: { $set: null },
            });
        },

        // Clear semua pilihan
        [ActionTypes.CLEAR_SELECTED_BILLS]: (state) => immutable(state, {
            status: { $set: 'idle' },
            selectedBills: {
                $set: {
                    items: [],
                    total: 0,
                    totalAmount: 0,
                    selectedIds: [],
                    summary: {
                        count: 0,
                        amount: 0,
                        categories: {},
                    }
                }
            },
            lastUpdated: { $set: new Date().toISOString() },
            error: { $set: null },
        }),

        // Set dari response API
        [ActionTypes.SET_SELECTED_BILLS_FROM_RESPONSE]: (state, { response }) => {
            let items = Array.isArray(response) ? response : [response];
            const total = items.length;
            const totalAmount = items.reduce((sum, bill) => sum + (bill.amount || 0), 0);
            const selectedIds = items.map(bill => bill.id);

            // Buat summary
            const categories = {};
            items.forEach(bill => {
                const category = bill.category || 'uncategorized';
                if (!categories[category]) {
                    categories[category] = {
                        count: 0,
                        amount: 0,
                        items: []
                    };
                }
                categories[category].count += 1;
                categories[category].amount += (bill.amount || 0);
                categories[category].items.push(bill);
            });

            return immutable(state, {
                status: { $set: total > 0 ? 'bills_selected' : 'idle' },
                selectedBills: {
                    $set: {
                        items: items,
                        total: total,
                        totalAmount: totalAmount,
                        selectedIds: selectedIds,
                        summary: {
                            count: total,
                            amount: totalAmount,
                            categories: categories,
                        }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
                error: { $set: null },
            });
        },

        // Update status loading
        [ActionTypes.BILL_LOADING]: (state) => immutable(state, {
            status: { $set: 'loading' },
            error: { $set: null },
        }),

        // Set error
        [ActionTypes.BILL_ERROR]: (state, { payload }) => immutable(state, {
            status: { $set: 'error' },
            error: { $set: payload },
        }),

        // Update quantity atau jumlah tagihan tertentu
        [ActionTypes.UPDATE_BILL_QUANTITY]: (state, { payload }) => {
            const { id, quantity } = payload;
            const currentItems = state.selectedBills.items || [];
            const updatedItems = currentItems.map(bill => {
                if (bill.id === id) {
                    return {
                        ...bill,
                        quantity: quantity,
                        amount: (bill.price || 0) * quantity
                    };
                }
                return bill;
            });

            const totalAmount = updatedItems.reduce((sum, bill) => sum + (bill.amount || 0), 0);

            return immutable(state, {
                selectedBills: {
                    items: { $set: updatedItems },
                    totalAmount: { $set: totalAmount },
                    summary: {
                        amount: { $set: totalAmount }
                    }
                },
                lastUpdated: { $set: new Date().toISOString() },
            });
        },
    },
    billState
);